import apiClient from '@/lib/api-client';
import type { PlatformUser } from '@/types/auth';

export interface PlatformLoginResult {
  accessToken: string;
  platformAdmin: { id: number; email: string; firstName?: string; lastName?: string };
}

export interface InvitationCodeCheck {
  valid: boolean;
  /** Make the code invalid. */
  problems: string[];
  /** Only affect some signups (e.g. a currency the plan has no price in); the code stays valid. */
  warnings?: string[];
}

export interface PlatformInvitationCode {
  id: number;
  code: string;
  stripeCodeId: string;
  subscriptionPlanId: number;
  planTarget: string | null;
  active: boolean;
  expiresAt: string | null;
  maxSignups: number | null;
  notes: string | null;
  signupsCount: number;
  grantedCount: number;
  /** Local state only (active, not expired, under the cap). */
  redeemable: boolean;
  /** Cached result of the last Stripe + plan check; null until one ran. */
  lastCheck: InvitationCodeCheck | null;
  lastCheckedAt: string | null;
  createdAt: string;
}

export interface PlatformPlanOption {
  id: number;
  code: string;
  name: string;
}

export interface InvitationCodeInput {
  code?: string;
  stripeCodeId?: string;
  subscriptionPlanId?: number;
  active?: boolean;
  /** ISO string, or null to clear. */
  expiresAt?: string | null;
  maxSignups?: number | null;
  notes?: string | null;
}

export interface InvitationCodeSignup {
  organizationId: number;
  organizationName: string;
  ownerId: number | null;
  ownerEmail: string | null;
  ownerFirstName: string | null;
  ownerLastName: string | null;
  signedUpAt: string;
  /** Set when the invited plan was granted; null when the signup fell back to the trial. */
  invitationCodeAppliedAt: string | null;
  subscriptionStatus: string | null;
  subscriptionPlan: string | null;
}

const mapInvitationCode = (c: any): PlatformInvitationCode => ({
  id: c.id,
  code: c.code,
  stripeCodeId: c.stripe_code_id,
  subscriptionPlanId: c.subscription_plan_id,
  planTarget: c.plan_target ?? null,
  active: c.active,
  expiresAt: c.expires_at ?? null,
  maxSignups: c.max_signups ?? null,
  notes: c.notes ?? null,
  signupsCount: c.signups_count ?? 0,
  grantedCount: c.granted_count ?? 0,
  redeemable: c.redeemable,
  lastCheck: c.last_check ?? null,
  lastCheckedAt: c.last_checked_at ?? null,
  createdAt: c.created_at,
});

// Only the keys the caller set are sent, so a PATCH never clears a field by omission.
const toInvitationCodePayload = (input: InvitationCodeInput) => {
  const payload: Record<string, unknown> = {};
  if (input.code !== undefined) payload.code = input.code;
  if (input.stripeCodeId !== undefined) payload.stripe_code_id = input.stripeCodeId;
  if (input.subscriptionPlanId !== undefined) payload.subscription_plan_id = input.subscriptionPlanId;
  if (input.active !== undefined) payload.active = input.active;
  if (input.expiresAt !== undefined) payload.expires_at = input.expiresAt;
  if (input.maxSignups !== undefined) payload.max_signups = input.maxSignups;
  if (input.notes !== undefined) payload.notes = input.notes;
  return payload;
};

export interface PlatformDashboard {
  usersCount: number;
  organizationsCount: number;
  invitedSignupsCount: number;
  invitedGrantedCount: number;
  invitationCodesCount: number;
  activeInvitationCodesCount: number;
  invalidInvitationCodesCount: number;
  activeImpersonationsCount: number;
}

export interface ImpersonateResult {
  accessToken: string;
  sessionId: number;
  user: { id: number; email: string; role: string; organizationId: number };
}

class PlatformService {
  async logout(): Promise<void> {
    try {
      await apiClient.post('/v1/platform/auth/logout');
    } catch {
      // Stateless endpoint — ignore failures, the client clears its token anyway.
    }
  }

  async login(email: string, password: string): Promise<PlatformLoginResult> {
    const { data } = await apiClient.post<any>('/v1/platform/auth/login', { email, password });
    const d = data.data;
    return {
      accessToken: d.access_token,
      platformAdmin: {
        id: d.platform_admin.id,
        email: d.platform_admin.email,
        firstName: d.platform_admin.first_name,
        lastName: d.platform_admin.last_name,
      },
    };
  }

  async listUsers(query = '', page = 1): Promise<{ users: PlatformUser[]; totalPages: number; currentPage: number }> {
    const { data } = await apiClient.get<any>('/v1/platform/users', { params: { q: query, page } });
    return {
      users: (data.data || []).map((u: any) => ({
        id: u.id,
        email: u.email,
        firstName: u.first_name,
        lastName: u.last_name,
        role: u.role,
        active: u.active,
        organizationId: u.organization_id,
        organizationName: u.organization_name,
      })),
      totalPages: data.meta?.total_pages ?? 1,
      currentPage: data.meta?.current_page ?? 1,
    };
  }

  async impersonate(userId: number): Promise<ImpersonateResult> {
    const { data } = await apiClient.post<any>(`/v1/platform/users/${userId}/impersonate`);
    const d = data.data;
    return {
      accessToken: d.access_token,
      sessionId: d.impersonation_session_id,
      user: {
        id: d.user.id,
        email: d.user.email,
        role: d.user.role,
        organizationId: d.user.organization_id,
      },
    };
  }

  async getDashboard(): Promise<PlatformDashboard> {
    const { data } = await apiClient.get<any>('/v1/platform/dashboard');
    const d = data.data;
    return {
      usersCount: d.users_count,
      organizationsCount: d.organizations_count,
      invitedSignupsCount: d.invited_signups_count,
      invitedGrantedCount: d.invited_granted_count,
      invitationCodesCount: d.invitation_codes_count,
      activeInvitationCodesCount: d.active_invitation_codes_count,
      invalidInvitationCodesCount: d.invalid_invitation_codes_count,
      activeImpersonationsCount: d.active_impersonations_count,
    };
  }

  async listInvitationCodes(): Promise<{ codes: PlatformInvitationCode[]; plans: PlatformPlanOption[] }> {
    const { data } = await apiClient.get<any>('/v1/platform/invitation_codes');
    return {
      codes: (data.data?.invitation_codes || []).map(mapInvitationCode),
      plans: data.data?.plans || [],
    };
  }

  async getInvitationCode(id: number): Promise<PlatformInvitationCode> {
    const { data } = await apiClient.get<any>(`/v1/platform/invitation_codes/${id}`);
    return mapInvitationCode(data.data);
  }

  async createInvitationCode(input: InvitationCodeInput): Promise<PlatformInvitationCode> {
    const { data } = await apiClient.post<any>('/v1/platform/invitation_codes', toInvitationCodePayload(input));
    return mapInvitationCode(data.data);
  }

  async updateInvitationCode(id: number, input: InvitationCodeInput): Promise<PlatformInvitationCode> {
    const { data } = await apiClient.patch<any>(`/v1/platform/invitation_codes/${id}`, toInvitationCodePayload(input));
    return mapInvitationCode(data.data);
  }

  /** Re-runs the Stripe coupon + plan check server-side. */
  async checkInvitationCode(id: number): Promise<PlatformInvitationCode> {
    const { data } = await apiClient.post<any>(`/v1/platform/invitation_codes/${id}/check`);
    return mapInvitationCode(data.data);
  }

  async listInvitationCodeSignups(
    id: number,
    page = 1,
  ): Promise<{ signups: InvitationCodeSignup[]; totalPages: number; currentPage: number; totalCount: number }> {
    const { data } = await apiClient.get<any>(`/v1/platform/invitation_codes/${id}/signups`, { params: { page } });
    return {
      signups: (data.data || []).map((s: any) => ({
        organizationId: s.organization_id,
        organizationName: s.organization_name,
        ownerId: s.owner_id ?? null,
        ownerEmail: s.owner_email ?? null,
        ownerFirstName: s.owner_first_name ?? null,
        ownerLastName: s.owner_last_name ?? null,
        signedUpAt: s.signed_up_at,
        invitationCodeAppliedAt: s.invitation_code_applied_at ?? null,
        subscriptionStatus: s.subscription_status ?? null,
        subscriptionPlan: s.subscription_plan ?? null,
      })),
      totalPages: data.meta?.total_pages ?? 1,
      currentPage: data.meta?.current_page ?? 1,
      totalCount: data.meta?.total_count ?? 0,
    };
  }

  // Stop must authenticate as the platform admin, whose token is stashed during
  // impersonation — send it explicitly rather than the active (impersonation) token.
  async stopImpersonation(sessionId: number, platformToken: string): Promise<void> {
    await apiClient.post(
      `/v1/platform/impersonations/${sessionId}/stop`,
      {},
      { headers: { Authorization: `Bearer ${platformToken}` } }
    );
  }
}

export const platformService = new PlatformService();
